import { getApiUrl } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/userUtils';


const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (response.ok) return data;

  const detail = data.detail;
  const error = new Error(
    (typeof detail === 'object' ? detail?.message : detail)
    || data.error
    || '파트너 정보를 처리하지 못했어요.',
  );
  error.code = (
    (typeof detail === 'object' ? detail?.code : null)
    || 'partner_request_failed'
  );
  error.status = response.status;
  throw error;
};


export const getPartnerProgram = async () => {
  const response = await fetch(getApiUrl('/api/partner/program'));
  const result = await parseResponse(response);
  if (
    result.success !== true
    || typeof result.terms_version !== 'string'
    || !Number.isFinite(result.strategic_royalty_per_sale)
  ) {
    throw new Error('파트너 조건 응답을 확인하지 못했어요.');
  }
  return result;
};


export const submitPartnerConfirmation = async (user, payload) => {
  if (!user?.getIdToken || user?.isTempReviewUser) {
    throw new Error('로그인 후 조건을 확인할 수 있어요.');
  }
  const headers = await getAuthHeaders(user);
  const response = await fetch(getApiUrl('/api/partner/confirmations'), {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const result = await parseResponse(response);
  if (
    result.success !== true
    || typeof result.confirmation_id !== 'string'
    || typeof result.created_now !== 'boolean'
  ) {
    throw new Error('조건 확인 저장 결과를 확인하지 못했어요.');
  }
  return result;
};


export const getPartnerDashboard = async (user) => {
  if (!user?.getIdToken || user?.isTempReviewUser) {
    throw new Error('로그인 후 정산 내역을 볼 수 있어요.');
  }
  const headers = await getAuthHeaders(user);
  const response = await fetch(getApiUrl('/api/partner/dashboard'), {
    headers,
  });
  const result = await parseResponse(response);
  if (
    result.success !== true
    || typeof result.partner_status !== 'string'
    || typeof result.has_sales_data !== 'boolean'
    || typeof result.this_month !== 'object'
    || !Array.isArray(result.monthly)
  ) {
    throw new Error('정산 내역 응답을 확인하지 못했어요.');
  }
  return result;
};


export const getPartnerStatus = async (user) => {
  if (!user?.getIdToken || user?.isTempReviewUser) {
    return { partner_status: 'not_active', is_active: false };
  }
  const headers = await getAuthHeaders(user);
  const response = await fetch(getApiUrl('/api/partner/status'), { headers });
  const result = await parseResponse(response);
  if (
    result.success !== true
    || typeof result.partner_status !== 'string'
    || typeof result.is_active !== 'boolean'
  ) {
    throw new Error('강사 계정 상태를 확인하지 못했어요.');
  }
  return result;
};


export const activatePartnerAccount = async (user, partnerUid, displayName) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(
    getApiUrl(`/api/partner/admin/accounts/${encodeURIComponent(partnerUid)}/activate`),
    {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ display_name: displayName }),
    },
  );
  return parseResponse(response);
};


export const uploadPartnerFinalContract = async (
  user,
  partnerUid,
  {
    contractPdf,
    contractVersion,
    signedAtIso,
  },
) => {
  const headers = await getAuthHeaders(user);
  const formData = new FormData();
  formData.append('contract_version', contractVersion);
  formData.append('signed_at_iso', signedAtIso);
  formData.append('contract_pdf', contractPdf);
  const response = await fetch(
    getApiUrl(
      `/api/partner/admin/accounts/${encodeURIComponent(partnerUid)}/final-contract`,
    ),
    {
      method: 'POST',
      headers,
      body: formData,
    },
  );
  return parseResponse(response);
};


export const uploadPartnerEmploymentApproval = async (
  user,
  partnerUid,
  {
    approvalPdf,
    approvedAtIso,
    conditionsSummary,
    expiresAtIso,
    issuedBy,
    status,
  },
) => {
  const headers = await getAuthHeaders(user);
  const formData = new FormData();
  formData.append('status', status);
  formData.append('issued_by', issuedBy);
  formData.append('approved_at_iso', approvedAtIso);
  formData.append('expires_at_iso', expiresAtIso || '');
  formData.append('conditions_summary', conditionsSummary || '');
  formData.append('approval_pdf', approvalPdf);
  const response = await fetch(
    getApiUrl(
      `/api/partner/admin/accounts/${encodeURIComponent(partnerUid)}/employment-approval`,
    ),
    {
      method: 'POST',
      headers,
      body: formData,
    },
  );
  return parseResponse(response);
};


export const getPartnerSettlements = async (user, period) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(
    getApiUrl(`/api/partner/admin/settlements?period=${encodeURIComponent(period)}`),
    { headers },
  );
  const result = await parseResponse(response);
  return result.items || [];
};


export const getPartnerConfirmations = async (user) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(
    getApiUrl('/api/partner/admin/confirmations?limit=100'),
    { headers },
  );
  const result = await parseResponse(response);
  return result.items || [];
};


export const markPartnerSettlementPaid = async (
  user,
  partnerUid,
  period,
) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(
    getApiUrl(
      `/api/partner/admin/accounts/${encodeURIComponent(partnerUid)}`
      + `/statements/${encodeURIComponent(period)}/paid`,
    ),
    { method: 'POST', headers },
  );
  return parseResponse(response);
};
